import { Link } from '@tanstack/react-router'
import type { AuthUser } from '../../lib/api'
import type { ThemeMode } from '../../lib/preferences'
import { Icon } from './Icon'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { useMessages } from '@/i18n/messages'
import { shellMessages } from '@/i18n/text/shell'

function initials(user: AuthUser): string {
  const name = user.claims?.displayName?.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return parts[0].slice(0, 2).toUpperCase()
  }
  return (user.email?.slice(0, 2) || '??').toUpperCase()
}

const iconBtn =
  'w-[38px] h-[38px] flex-none grid place-items-center rounded-[10px] border border-[var(--border)] bg-transparent text-[var(--fg-soft)] hover:bg-[var(--surface-2)] hover:text-[var(--fg)] transition'

export function Topbar({
  onToggleSidebar,
  onOpenSearch,
  canSearch,
  user,
  onLogout,
  theme,
  onToggleTheme,
}: {
  onToggleSidebar: () => void
  onOpenSearch: () => void
  canSearch: boolean
  user: AuthUser
  onLogout: () => void
  theme: ThemeMode
  onToggleTheme: () => void
}) {
  const m = useMessages(shellMessages)
  const roleLabel: Record<string, string> = { superadmin: 'Superadmin', admin: 'Admin', user: m.roleUser }
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)

  return (
    <header className="h-[60px] flex-none flex items-center gap-4 px-5 bg-[var(--surface)] border-b border-[var(--border)] z-10">
      <button onClick={onToggleSidebar} className={iconBtn} title={m.toggleMenu} aria-label={m.toggleMenu}>
        <Icon name="menu" className="w-[22px] h-[22px]" />
      </button>

      <Link to="/" className="flex items-center gap-3.5">
        <img src="/igf-logo.png" alt="Íslenska gámafélagið" className="h-[26px] w-auto block" />
      </Link>

      {canSearch ? (
        <button
          onClick={onOpenSearch}
          className="ml-2 sm:ml-6 flex flex-1 max-w-[440px] items-center gap-2.5 h-10 rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] px-3 text-[var(--faint)] hover:border-[var(--brand)] transition"
        >
          <Icon name="search" className="w-5 h-5" />
          <span className="flex-1 text-left text-sm font-medium truncate">{m.searchCustomer}</span>
          <kbd className="hidden sm:block font-grotesk font-semibold text-[11px] text-[var(--faint)] bg-[var(--surface)] border border-[var(--border)] rounded-md px-1.5 py-0.5">
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex-1 hidden md:block" />

      <LanguageSwitcher className="hidden sm:inline-flex" />

      <button onClick={onToggleTheme} className={iconBtn} title={m.toggleTheme} aria-label={m.toggleTheme}>
        <Icon name={theme === 'light' ? 'moon' : 'sun'} className="w-[21px] h-[21px]" />
      </button>

      <div className="flex items-center gap-2.5 pl-3.5 ml-1 border-l border-[var(--border)]">
        <div className="w-9 h-9 rounded-[10px] bg-[var(--brand)] text-[var(--on-brand)] grid place-items-center font-grotesk font-bold text-[13px]">
          {initials(user)}
        </div>
        <div className="hidden sm:flex flex-col leading-tight">
          <span className="font-bold text-[13px] text-[var(--fg)]">
            {user.claims?.displayName || user.email}
          </span>
          <span className="font-grotesk font-semibold text-[10px] uppercase tracking-wider text-[var(--brand)]">
            {roleLabel[user.role] ?? user.role}
          </span>
        </div>
        <button
          onClick={onLogout}
          className="ml-1 rounded-[10px] px-3 py-1.5 text-sm text-[var(--fg-soft)] hover:bg-[var(--surface-2)] transition"
          title={m.logout}
        >
          {m.logout}
        </button>
      </div>
    </header>
  )
}
